<script setup>
import { XMarkIcon } from "@heroicons/vue/24/solid";
import {
  formatNumber,
  formatPrice,
  formatPercentage,
} from "@js/utils/formatter";
import { computed } from "vue";

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  keyValue1: {
    type: String,
    required: true,
  },
  keyValue2: {
    type: String,
    required: true,
  },
  compareKeyValue1: {
    type: String,
    required: false,
    default: null,
  },
  compareKeyValue2: {
    type: String,
    required: false,
    default: null,
  },
  type: {
    type: String,
    required: false,
  },
  color: {
    type: Boolean,
    required: false,
    default: false,
  },
  reverseColor: {
    type: Boolean,
    required: false,
    default: false,
  },
  align: {
    type: String,
    required: false,
    default: "left",
  },
});

const formattedValue1 = computed(() => {
  if (props.data[props.keyValue1] === null) {
    return "";
  }

  if (props.type === "price") {
    return formatPrice(props.data[props.keyValue1], false);
  } else if (props.type === "percentage") {
    return formatPercentage(props.data[props.keyValue1], false, true);
  } else if (props.type === "number") {
    return formatNumber(props.data[props.keyValue1]);
  } else {
    return props.data[props.keyValue1];
  }
});

const formattedValue2 = computed(() => {
  if (props.data[props.keyValue2] === null) {
    return "";
  }

  if (props.type === "price") {
    return formatPrice(props.data[props.keyValue2], false);
  } else if (props.type === "percentage") {
    return formatPercentage(props.data[props.keyValue2], false, true);
  } else if (props.type === "number") {
    return formatNumber(props.data[props.keyValue2]);
  } else {
    return props.data[props.keyValue2];
  }
});

const classes = computed(() => {
  if (
    props.data[props.keyValue1] === null ||
    props.data[props.keyValue2] === null
  ) {
    return "";
  }

  const greenColor = "bg-green-400 text-white";
  const redColor = "bg-red-500 text-white";
  const leftAlign = "justify-start";
  const centerAlign = "items-center justify-center text-center";
  const rightAlign = "justify-end";

  let classes = "";

  if (props.align === "left") {
    classes += leftAlign;
  } else if (props.align === "center") {
    classes += centerAlign;
  } else if (props.align === "right") {
    classes += rightAlign;
  } else {
    classes += leftAlign;
  }

  if (props.color) {
    const value1 = props.compareKeyValue1
      ? props.data[props.compareKeyValue1]
      : props.data[props.keyValue1];

    const value2 = props.compareKeyValue2
      ? props.data[props.compareKeyValue2]
      : props.data[props.keyValue2];

    if (props.reverseColor) {
      if (value1 >= value2) {
        classes += " " + redColor;
      } else {
        classes += " " + greenColor;
      }
    } else {
      if (value1 >= value2) {
        classes += " " + greenColor;
      } else {
        classes += " " + redColor;
      }
    }
  }

  return classes;
});
</script>

<template>
  <div
    class="flex h-full flex-col whitespace-nowrap p-4"
    :class="[
      props.align === 'left' ? 'items-start' : '',
      props.align === 'center' ? 'items-center' : '',
      props.align === 'right' ? 'items-end' : '',
      classes,
    ]"
  >
    <template
      v-if="
        props.data[props.keyValue1] !== null &&
        props.data[props.keyValue2] !== null
      "
    >
      <div class="text-lg font-bold">
        {{ formattedValue1 }}
        <template v-if="props.compareKeyValue2 && props.data[props.compareKeyValue2]">
          <span class="text-sm font-normal">
            ({{ formatPercentage(props.data[props.compareKeyValue2], false, true) }})
          </span>
        </template>
      </div>

      <div>
        {{ formattedValue2 }}
      </div>
    </template>

    <template v-else>
      <XMarkIcon class="size-5 text-red-500" />
    </template>
  </div>
</template>
